import { SessionManager } from '../utils/SessionManager';

/**
 * グローバル並列数マネージャー
 * 複数ファイルアップロード時にリクエスト数が上限を超えないよう制御
 */
class GlobalConcurrencyManager {
  private static instance: GlobalConcurrencyManager;
  private activeRequests: number = 0;
  private maxGlobalConcurrent: number = 6; // ブラウザの同一ドメイン接続制限に合わせる
  private waitingQueue: Array<() => void> = [];

  private constructor() {}

  static getInstance(): GlobalConcurrencyManager {
    if (!GlobalConcurrencyManager.instance) {
      GlobalConcurrencyManager.instance = new GlobalConcurrencyManager();
    }
    return GlobalConcurrencyManager.instance;
  }

  /**
   * グローバル上限を設定
   */
  setMaxConcurrent(max: number): void {
    this.maxGlobalConcurrent = max;
  }

  /**
   * スロットを取得（空きがなければ待機）
   */
  async acquire(): Promise<void> {
    if (this.activeRequests < this.maxGlobalConcurrent) {
      this.activeRequests++;
      return Promise.resolve();
    }

    // 空きスロットを待つ
    return new Promise<void>((resolve) => {
      this.waitingQueue.push(() => {
        this.activeRequests++;
        resolve();
      });
    });
  }

  /**
   * スロットを解放
   */
  release(): void {
    this.activeRequests--;

    // 待機中のリクエストがあれば1つ実行
    if (this.waitingQueue.length > 0 && this.activeRequests < this.maxGlobalConcurrent) {
      const next = this.waitingQueue.shift();
      if (next) next();
    }
  }

  /**
   * 現在のアクティブリクエスト数を取得
   */
  getActiveCount(): number {
    return this.activeRequests;
  }

  /**
   * 利用可能なスロット数を取得
   */
  getAvailableSlots(): number {
    return Math.max(0, this.maxGlobalConcurrent - this.activeRequests);
  }
}

// グローバルマネージャーのシングルトンをエクスポート
export const globalConcurrencyManager = GlobalConcurrencyManager.getInstance();

// アップロードメタデータ
export interface UploadMetadata {
  title: string;
  description: string;
  visibility: string;
}

// チャンクアップロード設定
export interface ChunkUploadConfig {
  chunkSize: number; // 20MB
  maxConcurrentChunks: number; // インスタンスあたりの並列リクエスト数（グローバル制限内で）
  maxBatchSize: number; // 1リクエストあたりの最大チャンク数（上限200MB = 10チャンク）
  maxRetries: number; // 3
  retryDelay: number; // 1000ms
  initialBatchSize: number; // 初期バッチサイズ
  enableAdaptive: boolean; // 適応型調整を有効にするか
}

// イベントハンドラー型
type EventHandler = (...args: any[]) => void;

// イベント型
type UploadEvent = 'progress' | 'error' | 'complete' | 'paused' | 'resumed' | 'cancelled';

/**
 * チャンクアップロードマネージャークラス
 */
export class ChunkUploadManager {
  private file: File;
  private metadata: UploadMetadata;
  private config: ChunkUploadConfig;
  private sessionId: string | null = null;
  private totalChunks: number = 0;
  private uploadedChunks: Set<number> = new Set();
  private failedChunks: Map<number, number> = new Map(); // chunkIndex → retryCount
  private retryingChunks: Set<number> = new Set(); // リトライ待機中のチャンク
  private uploadQueue: number[] = [];
  private isPaused: boolean = false;
  private isCancelled: boolean = false;
  private isCompleting: boolean = false; // 完了処理中フラグ
  private hasEmittedError: boolean = false; // エラー発火済みフラグ
  private eventHandlers: Map<UploadEvent, EventHandler[]> = new Map();
  private currentBatchSize: number = 3; // 現在のバッチサイズ（動的に変化）
  private activeBatchUploads: Map<string, XMLHttpRequest> = new Map(); // バッチアップロード用

  // 帯域幅測定用
  private uploadSpeedHistory: number[] = []; // 過去のアップロード速度（bytes/sec）

  // ファイルハッシュ（セッション再開用）
  private fileHash: string | null = null;

  constructor(config?: Partial<ChunkUploadConfig>) {
    this.file = null!;
    this.metadata = null!;
    this.config = {
      chunkSize: 20 * 1024 * 1024, // 20MB
      maxConcurrentChunks: 5, // 並列リクエスト数（3→5に増加）
      maxBatchSize: 10, // 最大10チャンク = 200MB
      maxRetries: 3,
      retryDelay: 1000,
      initialBatchSize: 3, // 初期バッチサイズ（1→3に増加）
      enableAdaptive: true, // 適応型調整を有効
      ...config,
    };
  }

  /**
   * ファイルハッシュを計算（サンプリング方式）
   * 大容量ファイル対応: 先頭/中間/末尾のサンプル + ファイルサイズでハッシュ生成
   */
  private async calculateFileHash(file: File): Promise<string> {
    // crypto.subtle は Secure Context (HTTPS/localhost) でのみ利用可能
    // HTTP 環境ではファイルメタデータによる疑似ハッシュにフォールバック
    if (!crypto.subtle) {
      const pseudo = `${file.name}:${file.size}:${file.lastModified}`;
      let h = 0;
      for (let i = 0; i < pseudo.length; i++) {
        h = Math.imul(31, h) + pseudo.charCodeAt(i);
      }
      return (
        Math.abs(h).toString(16).padStart(8, '0') +
        file.size.toString(16).padStart(16, '0') +
        file.lastModified.toString(16).padStart(16, '0')
      );
    }

    const SAMPLE_SIZE = 1024 * 1024; // 1MB
    const samples: ArrayBuffer[] = [];

    // 先頭1MB
    samples.push(await file.slice(0, Math.min(SAMPLE_SIZE, file.size)).arrayBuffer());

    // ファイルサイズが2MB以上なら中間と末尾もサンプル
    if (file.size > SAMPLE_SIZE * 2) {
      const middle = Math.floor(file.size / 2);
      samples.push(await file.slice(middle, middle + SAMPLE_SIZE).arrayBuffer());
      samples.push(await file.slice(-SAMPLE_SIZE).arrayBuffer());
    }

    // ファイルサイズを含めてハッシュ
    const totalSampleSize = samples.reduce((acc, s) => acc + s.byteLength, 0);
    const combined = new Uint8Array(totalSampleSize + 8);
    const sizeView = new DataView(combined.buffer);
    sizeView.setBigUint64(0, BigInt(file.size), true);

    let offset = 8;
    for (const sample of samples) {
      combined.set(new Uint8Array(sample), offset);
      offset += sample.byteLength;
    }

    const hashBuffer = await crypto.subtle.digest('SHA-256', combined);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /**
   * 初期化
   */
  async initialize(file: File, metadata: UploadMetadata): Promise<void> {
    this.file = file;
    this.metadata = metadata;
    this.totalChunks = Math.ceil(file.size / this.config.chunkSize);

    // 状態をリセット
    this.uploadedChunks.clear();
    this.failedChunks.clear();
    this.retryingChunks.clear();
    this.activeBatchUploads.clear();
    this.isPaused = false;
    this.isCancelled = false;
    this.isCompleting = false;
    this.hasEmittedError = false;
    this.currentBatchSize = this.config.initialBatchSize;
    this.uploadSpeedHistory = [];
    this.fileHash = null;

    // 認証トークンを取得
    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      throw new Error('認証トークンが見つかりません');
    }

    // ハッシュ計算を待機
    this.fileHash = await this.calculateFileHash(this.file);

    // 新規セッションを作成（ハッシュ付き）
    const response = await fetch('/api/videos/upload/init', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${activeSession.token}`,
      },
      body: JSON.stringify({
        filename: file.name,
        fileSize: file.size,
        chunkSize: this.config.chunkSize,
        title: metadata.title,
        description: metadata.description,
        visibility: metadata.visibility,
        fileHash: this.fileHash,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      // 403 + INTERNAL_NETWORK_ONLY を呼び出し元が判定できるよう status/code を保持する
      // （汎用 Error message で潰さない / UX 仕様書 §2.9）
      const initError = new Error(
        errorData.error || 'セッション初期化に失敗しました'
      ) as Error & { status?: number; code?: string };
      initError.status = response.status;
      if (errorData.code) {
        initError.code = errorData.code;
      }
      throw initError;
    }

    const data = await response.json();
    this.sessionId = data.sessionId;

    // 既にアップロード済みのチャンクを記録
    if (data.uploadedChunks && data.uploadedChunks.length > 0) {
      data.uploadedChunks.forEach((index: number) => {
        this.uploadedChunks.add(index);
      });
    }

    // アップロードキューを初期化（未アップロードのチャンクのみ）
    this.uploadQueue = [];
    for (let i = 0; i < this.totalChunks; i++) {
      if (!this.uploadedChunks.has(i)) {
        this.uploadQueue.push(i);
      }
    }
  }

  /**
   * 非同期ハッシュ計算（バックグラウンドで実行）
   */
  private calculateFileHashAsync(): void {
    this.calculateFileHash(this.file)
      .then((hash) => {
        this.fileHash = hash;
        // オプション: 計算完了後にサーバーにハッシュを送信することも可能
      })
      .catch((err) => {
        console.warn('ハッシュ計算失敗:', err);
      });
  }

  /**
   * ファイルハッシュを取得
   */
  getFileHash(): string | null {
    return this.fileHash;
  }

  /**
   * 既存セッションの再開を試みる
   */
  async resumeSession(file: File, metadata: UploadMetadata): Promise<{
    sessionId: string;
    uploadedChunks: number[];
    totalChunks: number;
    chunkSize: number;
  } | null> {
    this.file = file;
    this.metadata = metadata;
    this.totalChunks = Math.ceil(file.size / this.config.chunkSize);

    // ハッシュ計算を待機（再開には必須）
    this.fileHash = await this.calculateFileHash(file);

    const activeSession = SessionManager.getActiveSession();
    if (!activeSession || !activeSession.token) {
      throw new Error('認証トークンが見つかりません');
    }

    const response = await fetch('/api/videos/upload/resume', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${activeSession.token}`,
      },
      body: JSON.stringify({
        filename: file.name,
        fileSize: file.size,
        fileHash: this.fileHash,
      }),
    });

    if (!response.ok) {
      console.warn('Resume endpoint failed, will create new session');
      return null;
    }

    const data = await response.json();

    // sessionIdが返されなければ既存セッションなし
    if (!data.sessionId) {
      return null;
    }

    // セッション発見 - 内部状態を初期化
    this.sessionId = data.sessionId;
    this.totalChunks = data.totalChunks;
    this.config.chunkSize = data.chunkSize;

    // 状態をリセット
    this.uploadedChunks.clear();
    this.failedChunks.clear();
    this.retryingChunks.clear();
    this.activeBatchUploads.clear();
    this.isPaused = false;
    this.isCancelled = false;
    this.isCompleting = false;
    this.hasEmittedError = false;
    this.currentBatchSize = this.config.initialBatchSize;
    this.uploadSpeedHistory = [];

    // アップロード済みチャンクを記録
    if (data.uploadedChunks && data.uploadedChunks.length > 0) {
      data.uploadedChunks.forEach((index: number) => {
        this.uploadedChunks.add(index);
      });
    }

    // 未アップロードチャンクのみキューに追加
    this.uploadQueue = [];
    for (let i = 0; i < this.totalChunks; i++) {
      if (!this.uploadedChunks.has(i)) {
        this.uploadQueue.push(i);
      }
    }

    return {
      sessionId: data.sessionId,
      uploadedChunks: data.uploadedChunks || [],
      totalChunks: data.totalChunks,
      chunkSize: data.chunkSize,
    };
  }

  /**
   * アップロード開始
   */
  async start(): Promise<void> {
    if (!this.sessionId) {
      throw new Error('セッションが初期化されていません');
    }

    this.isPaused = false;
    this.isCancelled = false;

    // アップロード完了まで待つPromiseを返す
    return new Promise<void>((resolve, reject) => {
      // 完了イベントハンドラ
      const onComplete = () => {
        cleanup();
        resolve();
      };

      // エラーイベントハンドラ
      const onError = (error: Error) => {
        cleanup();
        reject(error);
      };

      // キャンセルイベントハンドラ
      const onCancelled = () => {
        cleanup();
        reject(new Error('アップロードがキャンセルされました'));
      };

      // イベントハンドラのクリーンアップ
      const cleanup = () => {
        // 一度だけ実行されるようにハンドラを削除
        const completeHandlers = this.eventHandlers.get('complete');
        if (completeHandlers) {
          const index = completeHandlers.indexOf(onComplete);
          if (index > -1) completeHandlers.splice(index, 1);
        }

        const errorHandlers = this.eventHandlers.get('error');
        if (errorHandlers) {
          const index = errorHandlers.indexOf(onError);
          if (index > -1) errorHandlers.splice(index, 1);
        }

        const cancelledHandlers = this.eventHandlers.get('cancelled');
        if (cancelledHandlers) {
          const index = cancelledHandlers.indexOf(onCancelled);
          if (index > -1) cancelledHandlers.splice(index, 1);
        }
      };

      // イベントハンドラを登録
      this.on('complete', onComplete);
      this.on('error', onError);
      this.on('cancelled', onCancelled);

      // 並列アップロードを開始
      this.processQueue();
    });
  }

  /**
   * 一時停止
   */
  pause(): void {
    this.isPaused = true;

    // バッチアップロードを中断し、キューに戻す
    const abortedChunks: number[] = [];
    this.activeBatchUploads.forEach((xhr, batchId) => {
      // batchIdはチャンクインデックスを'-'で結合したもの (例: "0-1-2")
      const chunkIndices = batchId.split('-').map(Number);
      abortedChunks.push(...chunkIndices);
      xhr.abort();
    });
    this.activeBatchUploads.clear();

    // 中断されたチャンクをキューの先頭に戻す（優先的に再開するため）
    this.uploadQueue.unshift(...abortedChunks.sort((a, b) => a - b));

    this.emit('paused');
  }

  /**
   * 再開
   */
  async resume(): Promise<void> {
    if (!this.isPaused) {
      return;
    }

    this.isPaused = false;

    this.emit('resumed');

    // アップロードを再開
    this.processQueue();
  }

  /**
   * キャンセル
   */
  async cancel(): Promise<void> {
    this.isCancelled = true;
    this.isPaused = true;

    // バッチアップロードを中断
    this.activeBatchUploads.forEach((xhr) => {
      xhr.abort();
    });
    this.activeBatchUploads.clear();

    // リトライ待機中のチャンクをクリア
    this.retryingChunks.clear();

    // セッションをキャンセル
    if (this.sessionId) {
      const activeSession = SessionManager.getActiveSession();
      if (activeSession && activeSession.token) {
        try {
          await fetch(`/api/videos/upload/${this.sessionId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${activeSession.token}`,
            },
          });
        } catch (error) {
          console.error('セッションキャンセルエラー:', error);
        }
      }
    }

    this.emit('cancelled');
  }

  /**
   * キューを処理（バッチアップロード制御）
   */
  private processQueue(): void {
    if (this.isPaused || this.isCancelled) {
      return;
    }

    // インスタンスの上限とグローバルの空きスロットを考慮
    const instanceMax = this.config.maxConcurrentChunks || 5;
    const globalAvailable = globalConcurrencyManager.getAvailableSlots();
    const instanceAvailable = instanceMax - this.activeBatchUploads.size;
    const availableSlots = Math.min(globalAvailable, instanceAvailable);

    // 空きスロットがある限りバッチリクエストを投げる
    for (let i = 0; i < availableSlots && this.uploadQueue.length > 0; i++) {
      // 現在のバッチサイズ分のチャンクをキューから取得
      const batchChunks: number[] = [];
      const batchSize = Math.min(this.currentBatchSize, this.uploadQueue.length);

      for (let j = 0; j < batchSize; j++) {
        const chunkIndex = this.uploadQueue.shift();
        if (typeof chunkIndex !== 'undefined') {
          batchChunks.push(chunkIndex);
        }
      }

      if (batchChunks.length === 0) break;

      this.uploadBatchWithRetry(batchChunks);
    }

    // 完了判定：より厳密なチェックに変更
    const isAllUploaded = this.uploadedChunks.size === this.totalChunks;
    const isQueueEmpty = this.uploadQueue.length === 0;
    const isActiveEmpty = this.activeBatchUploads.size === 0;
    const isRetryingEmpty = this.retryingChunks.size === 0;
    const isValidTotal = this.totalChunks > 0;
    const hasNoFailedChunks = this.failedChunks.size === 0;

    if (isAllUploaded && isQueueEmpty && isActiveEmpty && isRetryingEmpty && isValidTotal && hasNoFailedChunks && !this.isCompleting) {
      this.isCompleting = true; // 完了処理中フラグを立てる
      this.completeUpload();
    } else if (!hasNoFailedChunks && isQueueEmpty && isActiveEmpty && isRetryingEmpty && !this.hasEmittedError) {
      // 全てのリトライが完了し、失敗チャンクが残っている場合のみエラーを発火
      this.hasEmittedError = true;
      const failedIndices = Array.from(this.failedChunks.keys());
      this.emit('error', new Error(`チャンク [${failedIndices.join(', ')}] のアップロードに失敗しました`));
    }
  }

  /**
   * 帯域幅を測定して適応型調整を行う
   */
  private measureAndAdaptBandwidth(uploadedBytes: number, durationMs: number): void {
    if (!this.config.enableAdaptive || durationMs <= 0) return;

    // アップロード速度を計算（bytes/sec）
    const speed = (uploadedBytes / durationMs) * 1000;
    this.uploadSpeedHistory.push(speed);

    // 直近5回の平均速度を計算
    const recentHistory = this.uploadSpeedHistory.slice(-5);
    const avgSpeed = recentHistory.reduce((a, b) => a + b, 0) / recentHistory.length;

    // 速度に基づいてバッチサイズを調整
    // 高速（50MB/s以上）: バッチサイズを積極的に増やす
    // 中速（20-50MB/s）: 緩やかに増やす
    // 低速（20MB/s未満）: 現状維持または減らす
    const speedMBps = avgSpeed / (1024 * 1024);

    if (speedMBps >= 50 && this.currentBatchSize < this.config.maxBatchSize) {
      // 高速: +2
      this.currentBatchSize = Math.min(this.currentBatchSize + 2, this.config.maxBatchSize);
    } else if (speedMBps >= 20 && this.currentBatchSize < this.config.maxBatchSize) {
      // 中速: +1
      this.currentBatchSize = Math.min(this.currentBatchSize + 1, this.config.maxBatchSize);
    } else if (speedMBps < 10 && this.currentBatchSize > 1) {
      // 低速: バッチサイズを減らす
      this.currentBatchSize = Math.max(1, this.currentBatchSize - 1);
    }
  }

  /**
   * バッチ（複数チャンク）をリトライ付きでアップロード
   */
  private uploadBatchWithRetry(chunkIndices: number[], attempt: number = 0): void {
    const batchId = chunkIndices.join('-');
    const uploadStartTime = Date.now();
    const uploadBytes = chunkIndices.length * this.config.chunkSize;

    this.uploadBatch(chunkIndices, batchId)
      .then(() => {
        // 帯域幅測定と適応型調整
        const duration = Date.now() - uploadStartTime;
        this.measureAndAdaptBandwidth(uploadBytes, duration);

        // 成功: 全チャンクをアップロード済みに追加
        chunkIndices.forEach((chunkIndex) => {
          this.uploadedChunks.add(chunkIndex);
          this.retryingChunks.delete(chunkIndex);
          this.failedChunks.delete(chunkIndex);
        });

        this.emitProgress();
        this.processQueue();
      })
      .catch(() => {
        // Abortによるエラーの場合はリトライしない
        if (this.isPaused || this.isCancelled) {
          chunkIndices.forEach((chunkIndex) => {
            this.retryingChunks.delete(chunkIndex);
          });
          return;
        }

        // リトライ時: バッチサイズを75%に減少（最小1）
        this.currentBatchSize = Math.max(1, Math.floor(this.currentBatchSize * 0.75));

        if (attempt < this.config.maxRetries) {
          const delay = this.config.retryDelay * Math.pow(2, attempt);

          // リトライ情報を記録
          chunkIndices.forEach((chunkIndex) => {
            this.failedChunks.set(chunkIndex, attempt + 1);
            this.retryingChunks.add(chunkIndex);
          });

          setTimeout(() => {
            if (!this.isPaused && !this.isCancelled) {
              this.uploadBatchWithRetry(chunkIndices, attempt + 1);
            } else {
              chunkIndices.forEach((chunkIndex) => {
                this.retryingChunks.delete(chunkIndex);
              });
            }
          }, delay);
          return;
        } else {
          // リトライ上限に達した場合
          chunkIndices.forEach((chunkIndex) => {
            this.failedChunks.set(chunkIndex, attempt + 1);
            this.retryingChunks.delete(chunkIndex);
          });

          this.processQueue();
          return;
        }
      });
  }

  /**
   * バッチ（複数チャンク）をアップロード
   */
  private async uploadBatch(chunkIndices: number[], batchId: string): Promise<void> {
    // グローバルスロットを取得
    await globalConcurrencyManager.acquire();

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('sessionId', this.sessionId!);

      // 複数チャンクをFormDataに追加
      chunkIndices.forEach((chunkIndex) => {
        const start = chunkIndex * this.config.chunkSize;
        const end = Math.min(start + this.config.chunkSize, this.file.size);
        const chunk = this.file.slice(start, end);
        formData.append('chunkIndices', chunkIndex.toString());
        formData.append('chunks', chunk, `chunk-${chunkIndex}`);
      });

      const xhr = new XMLHttpRequest();
      this.activeBatchUploads.set(batchId, xhr);

      const activeSession = SessionManager.getActiveSession();
      if (!activeSession?.token) {
        this.activeBatchUploads.delete(batchId);
        globalConcurrencyManager.release(); // スロット解放
        return reject(new Error('Auth Token Missing'));
      }

      xhr.open('POST', '/api/videos/upload/chunks', true);
      xhr.setRequestHeader('Authorization', `Bearer ${activeSession.token}`);

      // タイムアウト設定（バッチサイズに応じて増加: 60秒 × チャンク数）
      xhr.timeout = 60000 * chunkIndices.length;

      xhr.onload = () => {
        this.activeBatchUploads.delete(batchId);
        globalConcurrencyManager.release(); // スロット解放
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          const errorResponse = xhr.responseText || xhr.statusText;
          reject(new Error(`Server Error: ${xhr.status} - ${errorResponse}`));
        }
      };

      xhr.onerror = () => {
        this.activeBatchUploads.delete(batchId);
        globalConcurrencyManager.release(); // スロット解放
        reject(new Error('Network Error'));
      };

      xhr.onabort = () => {
        this.activeBatchUploads.delete(batchId);
        globalConcurrencyManager.release(); // スロット解放
        reject(new Error('Aborted'));
      };

      xhr.ontimeout = () => {
        this.activeBatchUploads.delete(batchId);
        globalConcurrencyManager.release(); // スロット解放
        reject(new Error('Timeout'));
      };

      xhr.send(formData);
    });
  }

  /**
   * アップロード完了処理
   */
  private async completeUpload(): Promise<void> {
    if (!this.sessionId) {
      return;
    }

    // 失敗したチャンクがある場合は完了処理を実行しない
    if (this.failedChunks.size > 0) {
      const failedIndices = Array.from(this.failedChunks.keys());
      this.emit('error', new Error(`チャンク [${failedIndices.join(', ')}] のアップロードに失敗しました。リトライ回数の上限に達しています。`));
      return;
    }

    try {
      const activeSession = SessionManager.getActiveSession();
      if (!activeSession || !activeSession.token) {
        throw new Error('認証トークンが見つかりません');
      }

      const response = await fetch('/api/videos/upload/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${activeSession.token}`,
        },
        body: JSON.stringify({
          sessionId: this.sessionId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'アップロード完了処理に失敗しました');
      }

      const data = await response.json();
      this.emit('complete', data.videoId);
    } catch (error) {
      this.emit('error', error);
    } finally {
      // 完了処理が失敗した場合でもフラグをリセットして再試行可能にする
      this.isCompleting = false;
    }
  }

  /**
   * 進捗イベントを発火
   */
  private emitProgress(): void {
    const progress = Math.round((this.uploadedChunks.size / this.totalChunks) * 100);
    this.emit('progress', progress, Array.from(this.uploadedChunks));
  }

  /**
   * 進捗率を取得
   */
  getProgress(): number {
    return Math.round((this.uploadedChunks.size / this.totalChunks) * 100);
  }

  /**
   * イベントハンドラーを登録
   */
  on(event: UploadEvent, handler: EventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * イベントを発火
   */
  private emit(event: UploadEvent, ...args: any[]): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => handler(...args));
    }
  }

  /**
   * アップロード済みチャンクを取得
   */
  getUploadedChunks(): number[] {
    return Array.from(this.uploadedChunks);
  }

  /**
   * 総チャンク数を取得
   */
  getTotalChunks(): number {
    return this.totalChunks;
  }

  /**
   * セッションIDを取得
   */
  getSessionId(): string | null {
    return this.sessionId;
  }
}
